import React, { useEffect, useRef, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { getPortraitViewportContainer } from "./PortraitStage";
import { getActivePinCode } from "../services/pinService";
import "./OTPModal.css";

function OTPModal(props) {
  const currentRefs = useRef([]);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [currentPin, setCurrentPin] = useState(Array(6).fill(""));
  const [pinError, setPinError] = useState("");
  const [correctPin, setCorrectPin] = useState(null);
  const [isLoadingPin, setIsLoadingPin] = useState(false);

  useEffect(() => {
    if (!props.show) {
      setCorrectPin(null);
      setCurrentPin(Array(6).fill(""));
      setPinError("");
      return undefined;
    }

    let cancelled = false;
    setIsLoadingPin(true);
    setPinError("");
    setCurrentPin(Array(6).fill(""));

    getActivePinCode()
      .then((result) => {
        if (cancelled) return;
        if (result.success && result.pinCode) {
          setCorrectPin(result.pinCode);
        } else {
          setPinError(result.error || "Failed to load PIN code. Please try again.");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPinError("Network error. Please check your connection and try again.");
        }
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoadingPin(false);
        window.setTimeout(() => currentRefs.current[0]?.focus(), 100);
      });

    return () => {
      cancelled = true;
    };
  }, [props.show]);

  useEffect(() => {
    const threshold = 150;
    const initialHeight = window.innerHeight;
    const handleResize = () => {
      setKeyboardOpen(initialHeight - window.innerHeight > threshold);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleChange = (e, index) => {
    const { value } = e.target;
    if (!/^\d*$/.test(value)) return;
    if (pinError) setPinError("");

    const updated = [...currentPin];
    updated[index] = value.slice(-1);
    setCurrentPin(updated);

    if (value && index < currentRefs.current.length - 1) {
      currentRefs.current[index + 1]?.focus();
    }

    if (!updated.every((d) => d !== "")) return;

    window.setTimeout(() => {
      const enteredPin = updated.join("");
      if (!correctPin) {
        setPinError("PIN code not loaded. Please wait...");
        setCurrentPin(Array(6).fill(""));
        return;
      }
      if (enteredPin === correctPin) {
        props.onSuccess?.();
        props.onHide?.();
        return;
      }
      setPinError("Incorrect PIN. Please try again.");
      setCurrentPin(Array(6).fill(""));
      window.setTimeout(() => currentRefs.current[0]?.focus(), 100);
    }, 100);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !currentPin[index] && index > 0) {
      currentRefs.current[index - 1]?.focus();
    }
  };

  return (
    <Modal
      {...props}
      container={() => getPortraitViewportContainer() ?? document.body}
      backdrop="static"
      className={`otpModal ${keyboardOpen ? "keyboard-open" : ""}`}
      keyboard={false}
      scrollable={false}
    >
      <Modal.Body>
        <div className="otpModalBody">
          <div>
            <button
              className="back p-1"
              type="button"
              aria-label="Close"
              onClick={() => props.onHide?.()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.52"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
          <div className="header">Enter your PIN code to access Admin Mode</div>
          {isLoadingPin ? (
            <div className="text-center mt-3">
              <div style={{ fontSize: 14, fontWeight: 500, color: "#ccc" }}>
                Loading PIN code...
              </div>
            </div>
          ) : (
            <>
              <div className="d-flex align-items-center justify-content-center gap-3">
                {currentPin.map((digit, index) => (
                  <Form.Control
                    key={index}
                    ref={(el) => {
                      currentRefs.current[index] = el;
                    }}
                    value={digit}
                    onChange={(e) => handleChange(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    maxLength={1}
                    className="px-1 text-center"
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    disabled={isLoadingPin || !correctPin}
                    autoComplete="off"
                  />
                ))}
              </div>
              {pinError ? (
                <div className="text-center mt-3">
                  <div
                    className="text-danger"
                    style={{ fontSize: 14, fontWeight: 500 }}
                  >
                    {pinError}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default OTPModal;
